const page = '/p';
const admin = '/a';

const query = (params: Record<string, string | undefined>) => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) searchParams.set(key, value);
  }
  return searchParams.size > 0 ? '?' + searchParams.toString() : '';
};

export const paths = {
  landing:
    import.meta.env.MODE === 'production'
      ? 'https://www.synoptic.com'
      : '/auth/login',

  home: `${page}/home`,
  explore: `${page}/home/explore`,

  privacyPolicy: '/policy',
  termsOfUse: '/terms',
  xAuth: '/xauth',

  login: '/auth/login',
  makeLogin: (from?: string, oauth?: string) =>
    `/auth/login${query({ from, oauth })}`,
  signup: '/auth/signup',
  makeSignup: (from?: string, oauth?: string) =>
    `/auth/signup${query({ from, oauth })}`,
  resetPassword: '/auth/reset-password',
  makeResetPassword: (from?: string, oauth?: string, username?: string) =>
    `/auth/reset-password${query({ from, oauth, username })}`,
  oauthCallback: '/auth/oauth-callback',
  makeOauthCallback: (from?: string, code?: string) =>
    `/auth/oauth-callback${query({ from, code, oauth: 'true' })}`,

  invite: `${page}/invite`,
  makeInvite: (code: string, invitedBy?: string) =>
    `${page}/invite${query({ code, invitedBy })}`,

  user: '/:username',
  makeUser: (name: string) => `/${name}`,
  userStreams: `/:username/streams`,
  makeUserStreams: (name: string) => `/${name}/streams`,
  followers: `/:username/followers`,
  makeFollowers: (name: string) => `/${name}/followers`,
  following: `/:username/following`,
  makeFollowing: (name: string) => `/${name}/following`,

  post: `${page}/posts/:id`,
  makePost: (postId: string) => `${page}/posts/${postId}`,

  tournaments: `${page}/tournaments`,
  makeTournaments: (category: string) =>
    `${page}/tournaments?category=${category}`,
  activeTournaments: `${page}/tournaments/active`,
  makeActiveTournaments: (category: string) =>
    `${page}/tournaments/active?category=${category}`,
  closedTournaments: `${page}/tournaments/closed`,
  makeClosedTournaments: (category: string) =>
    `${page}/tournaments/closed?category=${category}`,
  tournament: `${page}/tournaments/:id`,
  makeTournament: (id: string) => `${page}/tournaments/${id}`,
  submissions: `${page}/tournaments/:id/submissions`,
  makeSubmissions: (id: string) => `${page}/tournaments/${id}/submissions`,
  questionnaire: `${page}/tournaments/:id/questionnaire`,
  makeQuestionnaire: (id: string) => `${page}/tournaments/${id}/questionnaire`,
  participants: `${page}/tournaments/:id/participants`,
  makeParticipants: (id: string) => `${page}/tournaments/${id}/participants`,
  discussions: `${page}/tournaments/:id/discussions`,
  makeRules: (id: string) => `${page}/tournaments/${id}/rules`,
  rules: `${page}/tournaments/:id/rules`,
  makeDiscussions: (id: string) => `${page}/tournaments/${id}/discussions`,

  marketplace: `${page}/marketplace`,
  streams: `${page}/marketplace/streams`,
  makeStreams: (category: string) =>
    `${page}/marketplace/streams?category=${category}`,
  streamsFollowing: `${page}/marketplace/streams/following`,
  makeStreamsFollowing: (category: string) =>
    `${page}/marketplace/streams/following?category=${category}`,
  streamsCreated: `${page}/marketplace/streams/created`,
  makeStreamsCreated: (category: string) =>
    `${page}/marketplace/streams/created?category=${category}`,
  stream: `${page}/streams/:id`,
  makeStream: (id: string) => `${page}/streams/${id}`,
  makeStreamUnlock: (id: string) => `${page}/streams/${id}/unlock`,
  streamDiscussions: `${page}/streams/:id/discussions`,
  makeStreamDiscussions: (id: string) => `${page}/streams/${id}/discussions`,
  streamApi: `${page}/streams/:id/api`,
  makeStreamApi: (id: string) => `${page}/streams/${id}/api`,
  makeStreamReaderApi: (id: string) => `${page}/streams/${id}/reader-api`,
  makeStreamRegionStats: (id: string) => `${page}/streams/${id}/region-stats`,
  makeStreamStats: (id: string) => `${page}/streams/${id}/stats`,
  makeStreamRoutes: (id: string) => `${page}/streams/${id}/routes`,
  makeStreamExport: (id: string) => `${page}/streams/${id}/enterprise-export`,
  streamUsers: `${page}/streams/:id/users`,
  makeStreamUsers: (id: string) => `${page}/streams/${id}/users`,

  customStreams: `${page}/marketplace/custom-streams`,
  makeCustomStreams: (category: string) =>
    `${page}/marketplace/custom-streams?category=${category}`,
  customStreamsFollowing: `${page}/marketplace/custom-streams/following`,
  makeCustomStreamsFollowing: (category: string) =>
    `${page}/marketplace/custom-streams/following?category=${category}`,
  customStreamsCreated: `${page}/marketplace/custom-streams/created`,
  makeCustomStreamsCreated: (category: string) =>
    `${page}/marketplace/custom-streams/created?category=${category}`,
  streamTargets: `${page}/streams/:id/targets`,
  makeStreamTargets: (id: string) => `${page}/streams/${id}/targets`,

  streamPackages: `${page}/marketplace/stream-packages`,
  makeStreamPackages: (category: string) =>
    `${page}/marketplace/stream-packages?category=${category}`,
  subscribedStreamPackages: `${page}/marketplace/stream-packages/subscribed`,
  makeSubscribedStreamPackages: (category: string) =>
    `${page}/marketplace/stream-packages/subscribed?category=${category}`,
  createdStreamPackages: `${page}/marketplace/stream-packages/created`,
  makeCreatedStreamPackages: (category: string) =>
    `${page}/marketplace/stream-packages/created?category=${category}`,
  streamPackage: `${page}/stream-packages/:id`,
  makeStreamPackage: (id: string) => `${page}/stream-packages/${id}`,
  makeStreamPackageUnlock: (id: string) =>
    `${page}/stream-packages/${id}/unlock`,

  notifications: `${page}/notifications`,
  notificationInvites: `${page}/notifications/invites`,

  search: `${page}/search`,
  makeSearch: (q?: string, type?: string) =>
    `${page}/search${query({ q, t: type })}`,

  inviteFriend: `${page}/invite-friend`,

  settings: `${page}/settings`,
  settingsPersonalDetails: `${page}/settings/personal-details`,
  settingsConnectedAccounts: `${page}/settings/connected-accounts`,
  settingsSecurity: `${page}/settings/security`,
  settingsPassword: `${page}/settings/security/password`,
  settingsPayouts: `${page}/settings/payouts`,
  settingsApiKeys: `${page}/settings/api-keys`,

  notificationsSettings: `${page}/settings/notifications/all`,
  notificationsSettingsEmail: `${page}/settings/notifications/email`,
  notificationsSettingsInApp: `${page}/settings/notifications/in-app`,
  notificationsSettingsPush: `${page}/settings/notifications/push`,

  learningCenter: `${page}/learning-center`,

  newsDeck: `${page}/news-deck`,
  terminal: `${page}/terminal`,
  terminalSettings: `${page}/terminal/settings`,
  terminalSettingsTradingButtons: `${page}/terminal/settings/trading-buttons`,
  terminalSettingsExchangeCredentials: `${page}/terminal/settings/exchange-credentials`,
  terminalSettingsTradingKeywords: `${page}/terminal/settings/trading-keywords`,
  terminalSettingsStreamListFilters: `${page}/terminal/settings/stream-list-filters`,

  tools: `${page}/tools`,
  toolsTwttrApi: `${page}/tools/twttr-api`,
  toolsTwttrApiPrices: `${page}/tools/twttr-api/prices`,
  toolsTwttrApiSubscriptions: `${page}/tools/twttr-api/subscriptions`,
  toolsKiyotakaChart: `${page}/tools/kiyotaka-chart`,
  toolsDatabentoChart: `${page}/tools/databento-chart`,
  toolsTradesChart: `${page}/tools/trades-chart`,
  toolsStreamPostsSearch: `${page}/tools/stream-posts-search`,
  toolsVox: `${page}/tools/vox`,
  toolsVoxText: `${page}/tools/vox/text`,
  toolsVoxUsers: `${page}/tools/vox/users`,
  toolsVoxNames: `${page}/tools/vox/names`,
  toolsVoxSettings: `${page}/tools/vox/settings`,
  toolsVoxHistory: `${page}/tools/vox/history`,
  enrichers: `${page}/enrichers`,
  makeEnricher: (enricherId: string) => `${page}/enrichers/${enricherId}`,

  xGraph: `/ct`,
  ctStats: `/ct-stats`,

  enterprise: `/enterprise`,
  enterpriseStreams: `/enterprise/streams`,
  enterpriseLogin: '/enterprise/login',
} as const;

export const adminPaths = {
  dashboard: `${admin}`,
  streamsStats: `${admin}/streams-stats`,
  polymatcherInferences: `${admin}/polymatcher-inferences`,
  tournaments: `${admin}/tournaments`,
  tournament: `${admin}/tournaments/:id`,
  makeTournament: (id: string) => `${admin}/tournaments/${id}`,
  organizations: `${admin}/organizations`,
  organization: `${admin}/organizations/:id`,
  makeOrganization: (id: string) => `${admin}/organizations/${id}`,
  users: `${admin}/users`,
  userSubscriptions: `${admin}/users/:userId/subscriptions`,
  makeUserSubscriptions: (userId: string, username?: string) =>
    `${admin}/users/${userId}/subscriptions${username ? `?username=${username}` : ''}`,
  vox: `${admin}/vox`,
  sources: `${admin}/sources`,
  source: `${admin}/sources/:sourceId`,
  makeSource: (sourceId: string) => `${admin}/sources/${sourceId}`,
  makeSourceRoutes: (sourceId: string) => `${admin}/sources/${sourceId}/routes`,
  makeSourceTags: (sourceId: string) => `${admin}/sources/${sourceId}/tags`,
  makeSourceEnrichments: (sourceId: string) =>
    `${admin}/sources/${sourceId}/enrichments`,
  makeSourceApiKeys: (sourceId: string) =>
    `${admin}/sources/${sourceId}/api-keys`,
  makeSourceHistory: (sourceId: string) =>
    `${admin}/sources/${sourceId}/history`,
  makeSourceDocs: (sourceId: string) => `${admin}/sources/${sourceId}/docs`,
} as const;
